/**
 * OAuth Callback Page — GenesisBox
 * Handles the redirect from the configured auth providers.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useAuth } from '@hooks/auth/useAuth';
import { useOrg } from '@hooks/auth/useOrg';
import { verifyState } from '@config/oauth';
import { config } from '@config/environment';
import { setActiveOrgId } from '@services/storage/savedConnectionsStorage';

export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { setOrg } = useOrg();
  const [error, setError] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    const state = searchParams.get('state');

    if (!token) {
      setError('No token received from the server. Please try signing in again.');
      return;
    }

    if (state && !verifyState(state)) {
      setError('Security check failed (state mismatch). Please try again.');
      return;
    }

    (async () => {
      try {
        await login(token, '');

        const res = await fetch(`${config.apiBaseUrl}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          setError('Failed to verify session. Please sign in again.');
          return;
        }

        const data = await res.json();
        setOrg({
          id: data.org.id,
          name: data.org.name,
          role: data.org.role,
          storageBackend: data.org.storageBackend,
          memberCount: data.org.memberCount,
          isApproved: !!data.org.isApproved,
          status: data.org.status || 'pending',
          isAdmin: !!data.org.isAdmin,
          adminEnabled: !!data.org.adminEnabled,
          platformRole: data.org.platformRole === 'platform_owner' ? 'platform_owner' : null,
          canManageMembers: !!data.org.canManageMembers,
          orgs: data.orgs || [],
        });
        setActiveOrgId(data.org.id);

        navigate('/drive', { replace: true });
      } catch (err: any) {
        setError(err.message || 'Authentication failed.');
      }
    })();
  }, [searchParams, login, setOrg, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2,
        bgcolor: 'transparent',
      }}
    >
      {error ? (
        <Alert severity="error" className="glass-card" sx={{ maxWidth: 440, borderRadius: 3 }}>
          {error}
        </Alert>
      ) : (
        <>
          <CircularProgress sx={{ color: '#8ff5ff' }} />
          <Typography className="font-label" sx={{ fontSize: 11, color: '#8ff5ff' }}>
            Authenticating...
          </Typography>
        </>
      )}
    </Box>
  );
}
